import { RequestError } from '@/app/common/types_and_interfaces.ts'
import { currencyFormatter } from '@/lib/utils.ts'

/**
 * `insufficient_funds` is the only error that carries the aggregated award total, so the detail
 * key doubles as the discriminator. Every other purse error (no active term, term system not
 * configured, unauthorized, purse not found) already ships a user facing message.
 */
const INSUFFICIENT_FUNDS_DETAIL_KEY = 'total_award_amount'

/**
 * Sent when an explicit `student_ids` list contains a student who is not in Awarded status on the
 * selected purse for the active term. The API rejects the batch as a whole, so nobody is funded.
 */
const INELIGIBLE_STUDENTS_DETAIL_KEY = 'invalid_student_ids'

export function getIneligibleStudentIds(
  error: Pick<RequestError, 'details'>
): number[] | null {
  const rawIds = error.details?.[INELIGIBLE_STUDENTS_DETAIL_KEY]

  if (!Array.isArray(rawIds)) {
    return null
  }

  const ids = rawIds
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)

  return ids.length > 0 ? ids : null
}

export function getInsufficientFundsAmount(
  error: Pick<RequestError, 'details'>
): number | null {
  const rawAmount = error.details?.[INSUFFICIENT_FUNDS_DETAIL_KEY]

  if (
    (typeof rawAmount !== 'number' && typeof rawAmount !== 'string') ||
    (typeof rawAmount === 'string' && rawAmount.trim() === '')
  ) {
    return null
  }

  const amount = Number(rawAmount)

  return Number.isFinite(amount) ? amount : null
}

export function getFundAwardedStudentsErrorMessage(
  error: RequestError,
  purseName: string
): string {
  const totalAwardAmount = getInsufficientFundsAmount(error)

  if (totalAwardAmount !== null) {
    return `The awarded students on "${purseName}" require ${currencyFormatter.format(
      totalAwardAmount
    )}, which exceeds the available purse balance. No student was funded. Add funds to the purse and try again.`
  }

  const ineligibleStudentIds = getIneligibleStudentIds(error)

  if (ineligibleStudentIds !== null) {
    const isSingleStudent = ineligibleStudentIds.length === 1

    return `Student${isSingleStudent ? '' : 's'} ${ineligibleStudentIds.join(
      ', '
    )} ${
      isSingleStudent ? 'is' : 'are'
    } not in Awarded status on "${purseName}" for the active term. No student was funded — deselect ${
      isSingleStudent ? 'them' : 'those students'
    } or choose the purse they were awarded from.`
  }

  return error.message
}
